import React from "react";
import { Alert, List, Table, Typography } from "antd";

const { Title, Paragraph, Text } = Typography;

const Steps = ({ items }) => (
  <List
    size="small"
    dataSource={items}
    renderItem={(item, index) => (
      <List.Item>
        <Text strong style={{ marginRight: 8 }}>
          {index + 1}.
        </Text>
        {item}
      </List.Item>
    )}
  />
);

const CARRIERS = [
  {
    key: "monday",
    day: "Maandag",
    carrier: "Trunkrs",
    window: "17:00 - 22:00, de klant kiest geen tijdvak",
    export: "Exporteren → Trunkrs (maandag), op maandag zelf",
  },
  {
    key: "weekdays",
    day: "Dinsdag t/m zaterdag",
    carrier: "SooCool",
    window: "Het tijdvak dat de klant koos (08:00-18:00, 08:00-22:00, 08:00-12:00, 12:00-17:00 of 17:00-22:00)",
    export: "Exporteren → SooCool, op de werkdag vóór de bezorgdag, na 12:00",
  },
  {
    key: "sunday",
    day: "Zondag",
    carrier: "-",
    window: "Er wordt niet bezorgd",
    export: "-",
  },
];

/**
 * In-app documentation for sending orders: which carrier on which day, the daily routine with
 * SooCool (export, labels, pickup), cancelling, and what to do when something goes wrong.
 * Background and decisions: planning/soocool-koppeling.md.
 */
const ShippingDocumentation = () => (
  <div>
    <Title level={2}>Verzenden</Title>
    <Paragraph>
      Elke bezorgdag heeft één vervoerder. Op maandag is dat Trunkrs, van dinsdag t/m zaterdag SooCool. Beide shops
      (VCE en HVO) gaan met dezelfde vervoerder, vanuit hetzelfde warehouse. Afhalen werkt zoals altijd.
    </Paragraph>
    <Table
      size="small"
      pagination={false}
      rowKey="key"
      dataSource={CARRIERS}
      columns={[
        { title: "Bezorgdag", dataIndex: "day" },
        { title: "Vervoerder", dataIndex: "carrier" },
        { title: "Bezorgmoment", dataIndex: "window" },
        { title: "Versturen", dataIndex: "export" },
      ]}
    />
    <Paragraph style={{ marginTop: 12 }}>
      Een zaterdagbezorging gaat dus op vrijdag naar SooCool, en een dinsdagbezorging op maandag. Voor maandag geldt
      een daglimiet (VCE 50, HVO 25 bezorgorders); is die vol, dan kan de klant maandag niet meer kiezen.
    </Paragraph>

    <Title level={3}>Elke werkdag: de bestellingen van morgen naar SooCool</Title>
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 12 }}
      message="De labels moeten om 16:00 op de dozen zitten"
      description="SooCool haalt op de werkdag vóór de bezorgdag op bij het warehouse (Zoutketen 41, Enkhuizen), tussen 16:00 en 17:00. De chauffeur scant elke doos; zo ziet SooCool meteen welke bestellingen binnen zijn en of het aantal dozen klopt."
    />
    <Steps
      items={[
        <span>
          <Text strong>Na 12:00</Text>: dan kan er voor morgen niet meer besteld worden. Ga naar{" "}
          <Text strong>Bestellingen</Text> en kies de <Text strong>bezorgdatum</Text> (dus morgen, niet vandaag).
        </span>,
        <span>
          <Text strong>Exporteren → SooCool (Landelijk + Regionaal)</Text>. Zonder selectie gaan alle betaalde
          verzendbestellingen van die datum mee, van beide shops; met een selectie alleen die. Je krijgt per bestelling
          te zien of hij is aangemaakt, overgeslagen of mislukt, met de reden.
        </span>,
        <span>
          <Text strong>Printen → Verzendlabel(s) → SooCool labels (alle colli)</Text>. Je krijgt per shop een PDF met
          één A6-label per doos. Een bestelling boven de 15 kg is in meerdere dozen verdeeld en heeft dus meerdere
          labels.
        </span>,
        <span>
          <Text strong>Plak elk label op de goede doos</Text> en zet de dozen klaar voor de chauffeur.
        </span>,
      ]}
    />
    <Alert
      type="info"
      showIcon
      style={{ marginTop: 12 }}
      message="Op vrijdag staan de zaterdagbestellingen er ook bij"
      description="Kies je op de bestellingenpagina een vrijdag, dan toont hij vrijdag én zaterdag. Op donderdag verstuur je alleen de vrijdagbestellingen: selecteer die rijen eerst. Op vrijdag kies je de zaterdag zelf."
    />

    <Title level={3}>Vooruit exporteren</Title>
    <Paragraph>
      Dat mag: je kunt ook bestellingen voor over een paar dagen al naar SooCool sturen. SooCool haalt ze toch pas op de
      werkdag vóór de bezorgdag op, tussen 16:00 en 17:00. Exporteer je op maandag de bestellingen voor donderdag, dan
      komt SooCool woensdag ophalen. Let wel op:
    </Paragraph>
    <List
      size="small"
      dataSource={[
        <span>
          <Text strong>Wijzigingen daarna gaan niet mee.</Text> Bij de export liggen de dozen, het adres en het tijdvak
          vast. Pas je de bestelling daarna aan, annuleer hem dan op de pagina SooCool, exporteer opnieuw en print nieuwe
          labels. Is de bezorging bij SooCool al ingepland, dan kan annuleren niet meer.
        </span>,
        <span>
          <Text strong>Later binnengekomen bestellingen</Text> voor die dag (tot 12:00 de dag ervoor) moet je nog
          exporteren. Gewoon opnieuw exporteren: wat al bij SooCool staat, wordt overgeslagen.
        </span>,
        <span>
          <Text strong>Bewaar de labels</Text> tot de dozen van die bezorgdag gepickt zijn, en plak ze niet op dozen van
          een andere dag.
        </span>,
        <span>
          <Text strong>Feestdagen</Text> kent het systeem niet. Valt de werkdag vóór de bezorgdag op een feestdag, stem
          het ophalen dan af met SooCool.
        </span>,
      ]}
      renderItem={(item) => <List.Item>{item}</List.Item>}
    />

    <Title level={3}>Maandag: Trunkrs</Title>
    <Steps
      items={[
        <span>
          Kies op maandag de datum van vandaag en gebruik <Text strong>Exporteren → Trunkrs (maandag)</Text>. Alle
          verzendbestellingen van maandag gaan mee, ook regionaal en België. De klant krijgt van ons de mail dat Trunkrs
          de bestelling heeft opgehaald.
        </span>,
        <span>
          Wil je een bestelling voor dinsdag t/m zaterdag via Trunkrs sturen, dan vraagt het CMS eerst om bevestiging.
          Doe dat alleen als SooCool die dag niet kan rijden.
        </span>,
        <span>
          De SooCool-export slaat maandagbestellingen over, met de melding dat ze via Trunkrs moeten.
        </span>,
      ]}
    />

    <Title level={3}>Na het versturen</Title>
    <List
      size="small"
      dataSource={[
        <span>
          <Text strong>Status:</Text> na de export staat een bestelling op Verzonden. Zodra SooCool hem bezorgd heeft,
          zet het systeem hem zelf op Voltooid.
        </span>,
        <span>
          <Text strong>Bij de bestelling</Text> (klik op de rij) staat de SooCool-zending: de track & trace, het geplande
          tijdvenster, de dozen, en of er bij de buren is afgegeven of iets misging (niet thuis, beschadigd, geweigerd,
          verkeerd adres).
        </span>,
        <span>
          <Text strong>De klant</Text> krijgt van ons een mail met de track & trace zodra SooCool de bezorging heeft
          ingepland, met het verwachte tijdvenster, in de naam en stijl van de eigen webshop. Een "opgehaald"-mail
          sturen we bij SooCool niet. De Trustpilot-uitnodiging gaat mee met de SooCool-export.
        </span>,
      ]}
      renderItem={(item) => <List.Item>{item}</List.Item>}
    />

    <Title level={3}>De pagina SooCool: opzoeken en annuleren</Title>
    <Paragraph>
      Hier zie je wat SooCool zelf heeft staan, zonder in te loggen bij SooCool. Kies de webshop en zoek op bezorgdatum,
      ophaaldatum, bestelnummer of barcode. Elke zending toont een ophaaltaak (bij het warehouse) en een bezorgtaak (bij
      de klant).
    </Paragraph>
    <Steps
      items={[
        <span>
          <Text strong>Annuleren</Text>: vink de zendingen aan en klik op <Text strong>Annuleren bij SooCool</Text>. Na
          bevestiging annuleert SooCool ze en komt de bestelling in het CMS vrij: die kun je opnieuw exporteren, naar
          SooCool of Trunkrs.
        </span>,
        <span>
          Is de bezorging al ingepland, dan weigert SooCool het annuleren. Dat zie je bij die zending; neem dan contact op
          met SooCool.
        </span>,
        <span>
          Exporteer je een geannuleerde bestelling opnieuw, <Text strong>print dan ook de labels opnieuw</Text>: de oude
          labels horen bij de geannuleerde zending.
        </span>,
      ]}
    />

    <Title level={3}>Wat er op de achtergrond gebeurt</Title>
    <Paragraph>
      Niets hiervan hoef je zelf te doen; het staat hier zodat je weet waar wat vandaan komt.
    </Paragraph>
    <List
      size="small"
      dataSource={[
        <span>
          <Text strong>Bij het exporteren</Text> rekent het systeem per bestelling de dozen uit: hoogstens 15 kg per
          doos, op basis van de gewichten van de producten. Elke doos krijgt een barcode (bestelnummer-1, -2, …) en een
          omschrijving van de inhoud. SooCool krijgt per bestelling twee taken met dezelfde dozen: ophalen bij het
          warehouse en bezorgen bij de klant, in het tijdvak dat de klant koos.
        </span>,
        <span>
          <Text strong>Dubbel boeken kan niet.</Text> Een bestelling die al een SooCool-zending heeft, wordt
          overgeslagen. Gaat er tijdens het versturen iets mis met de verbinding, dan kijkt het systeem eerst bij
          SooCool of de zending er toch al is, voordat het opnieuw probeert.
        </span>,
        <span>
          <Text strong>SooCool stuurt updates terug</Text> (de webhook). Elke keer dat er iets verandert, bijvoorbeeld
          ingepland, opgehaald, bezorgd, niet gelukt of een nieuw tijdstip, stuurt SooCool de hele zending naar ons. Het
          systeem werkt dan de zending bij de bestelling bij. Daarom zie je bij een bestelling zonder iets te doen het
          geplande tijdvenster, de track & trace en eventuele problemen.
        </span>,
        <span>
          <Text strong>De track & trace-mail</Text> gaat uit op het moment dat SooCool de bezorging inplant: één keer per
          bezorgpoging. Komt er na een mislukte bezorging een nieuwe poging, dan krijgt de klant een nieuwe mail met de
          nieuwe link. Het ophalen bij het warehouse krijgt geen mail.
        </span>,
        <span>
          <Text strong>De status volgt de bezorging.</Text> Is de bezorging gelukt, dan gaat de bestelling van Verzonden
          naar Voltooid. Plant SooCool na een mislukte poging een nieuwe in, dan gaat hij terug naar Verzonden. Het
          ophalen bij het warehouse telt daarbij niet mee: opgehaald is nog niet bezorgd.
        </span>,
        <span>
          <Text strong>Historie:</Text> het versturen, elke update van SooCool, het bezorgen en het annuleren komen in de
          historie van de bestelling te staan, met de tijd en (bij annuleren) wie het deed.
        </span>,
        <span>
          <Text strong>Komt een update niet aan</Text>, bijvoorbeeld omdat onze server even onbereikbaar is, dan probeert
          SooCool het later opnieuw, tot tien keer. Gaat er bij ons iets mis met het verwerken, dan krijgt Digital KISS
          daar een melding van.
        </span>,
        <span>
          <Text strong>Maandaglimiet:</Text> het systeem zet elke nacht voor elke maandag tot een half jaar vooruit de
          limiet klaar (VCE 50, HVO 25 bezorgorders). Een aangepaste limiet blijft staan.
        </span>,
      ]}
      renderItem={(item) => <List.Item>{item}</List.Item>}
    />

    <Title level={3}>Als er iets misgaat</Title>
    <List
      size="small"
      dataSource={[
        <span>
          <Text strong>"Al naar SooCool gestuurd"</Text>: de bestelling heeft al een zending. Twee keer exporteren boekt
          dus nooit dubbel. Moet hij opnieuw, annuleer hem dan eerst op de pagina SooCool.
        </span>,
        <span>
          <Text strong>Mislukt door het adres</Text> ("Cannot find an exact match" of iets over postcode of huisnummer):
          controleer het adres, pas het aan met <Text strong>Bewerken</Text> bij de bestelling en exporteer die
          bestelling opnieuw.
        </span>,
        <span>
          <Text strong>Al bij Trunkrs</Text>: een bestelling die bij Trunkrs ligt, gaat niet ook naar SooCool (en
          andersom).
        </span>,
        <span>
          <Text strong>Een fout met 401 of de api-sleutel</Text>: dan praat het systeem niet met SooCool. Dat los je niet
          vanuit het CMS op; meld het bij Digital KISS.
        </span>,
        <span>
          <Text strong>Een doos klopt niet</Text>: het gewicht per doos komt uit het pickgewicht van de producten. Een
          product zonder pickgewicht telt als 0 gram, en dan kan een doos zwaarder zijn dan het label zegt. Vul het
          pickgewicht aan op de pagina <Text strong>Productgegevens</Text>.
        </span>,
      ]}
      renderItem={(item) => <List.Item>{item}</List.Item>}
    />
  </div>
);

export default ShippingDocumentation;
